import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

export default function Hero() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center transition-transform duration-[20s] ease-linear hover:scale-105"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/e7412743feb128461a12afd1376a9c95.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
      </div>

      {/* Floating Festive Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-[10%] text-4xl animate-float opacity-20" style={{ animationDelay: '0s' }}>❄</div>
        <div className="absolute top-40 right-[15%] text-3xl animate-float opacity-15" style={{ animationDelay: '1s' }}>✨</div>
        <div className="absolute top-60 left-[20%] text-2xl animate-twinkle opacity-20" style={{ animationDelay: '0.5s' }}>⭐</div>
        <div className="absolute bottom-40 right-[25%] text-4xl animate-float opacity-15" style={{ animationDelay: '1.5s' }}>🎄</div>
        <div className="absolute bottom-60 left-[30%] text-3xl animate-twinkle opacity-20" style={{ animationDelay: '2s' }}>✨</div>
        <div className="absolute top-1/3 right-[10%] text-2xl animate-sparkle opacity-15" style={{ animationDelay: '0.8s' }}>❄</div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white space-y-8">
            <div 
              className={`inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 transition-all duration-700 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <span className="text-[#D4AF37] font-semibold flex items-center gap-2">
                <span className="animate-bounce-soft inline-block">🎄</span> 
                2026's Top Gift Picks
                <span className="animate-sparkle inline-block">✨</span>
              </span>
            </div>
            
            <h1 
              className={`text-5xl lg:text-7xl font-bold leading-tight transition-all duration-700 delay-100 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Find the Perfect Christmas Gift for Everyone
            </h1>
            
            <p 
              className={`text-xl lg:text-2xl text-gray-200 leading-relaxed transition-all duration-700 delay-200 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Discover expertly curated gift guides, honest reviews, and the best Christmas presents for every person on your list. Make holiday shopping easy and joyful.
            </p>

            <div 
              className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-300 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <Link 
                to="/gift-guides"
                className="btn-festive inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-[#8B2635] to-[#A52A3A] text-white rounded-full font-semibold text-lg hover:shadow-2xl hover:shadow-[#8B2635]/30 transition-all duration-300 hover:scale-105 whitespace-nowrap group animate-pulse-glow"
              >
                Start Gift Hunting
                <i className="ri-sparkling-fill ml-2 group-hover:rotate-12 group-hover:scale-125 transition-transform"></i>
              </Link>
              <Link 
                to="/reviews"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg border-2 border-white/30 hover:bg-white/20 hover:border-[#D4AF37] transition-all duration-300 whitespace-nowrap group"
              >
                Browse Reviews
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
              </Link>
            </div>

            <div 
              className={`flex items-center gap-8 pt-4 transition-all duration-700 delay-[400ms] ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="flex items-center gap-2 group cursor-pointer">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div 
                      key={i} 
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B2635] border-2 border-white transition-transform duration-300 group-hover:scale-110"
                      style={{ transitionDelay: `${i * 50}ms` }}
                    ></div>
                  ))}
                </div>
                <span className="text-sm font-medium">500+ Gifts Reviewed</span>
              </div>
              <div className="flex items-center gap-2 group cursor-pointer">
                <div className="flex text-[#D4AF37]">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <i 
                      key={i} 
                      className="ri-star-fill transition-transform duration-300 group-hover:scale-125"
                      style={{ transitionDelay: `${i * 50}ms` }}
                    ></i>
                  ))}
                </div>
                <span className="text-sm font-medium">Trusted by 50K+ Shoppers</span>
              </div>
            </div>
          </div>

          <div 
            className={`hidden lg:block relative transition-all duration-1000 delay-500 ${
              isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-72 h-72 bg-[#D4AF37]/20 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-4 -left-4 w-72 h-72 bg-[#8B2635]/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
              
              <div className="relative grid grid-cols-2 gap-6">
                <div className="space-y-6">
                  <Link to="/gift-guides/luxury-gifts" className="block">
                    <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 hover:scale-105 hover:bg-white/15 transition-all duration-500 cursor-pointer group hover-glow">
                      <div className="w-full h-48 bg-gradient-to-br from-[#D4AF37] to-[#8B2635] rounded-2xl mb-4 flex items-center justify-center overflow-hidden">
                        <i className="ri-gift-fill text-6xl text-white group-hover:scale-110 group-hover:rotate-12 transition-transform duration-500"></i>
                      </div>
                      <h3 className="text-white font-bold text-lg mb-2 group-hover:text-[#D4AF37] transition-colors">Luxury Gifts</h3>
                      <p className="text-gray-300 text-sm">Premium presents that impress</p>
                    </div>
                  </Link>
                  <Link to="/gift-guides/tech-under-100" className="block" style={{ transitionDelay: '100ms' }}>
                    <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 hover:scale-105 hover:bg-white/15 transition-all duration-500 cursor-pointer group hover-glow">
                      <div className="w-full h-32 bg-gradient-to-br from-[#1B4332] to-[#2D5F4C] rounded-2xl mb-4 flex items-center justify-center overflow-hidden">
                        <i className="ri-smartphone-fill text-4xl text-white group-hover:scale-110 transition-transform duration-500"></i>
                      </div>
                      <h3 className="text-white font-bold group-hover:text-[#D4AF37] transition-colors">Tech Gifts</h3>
                      <p className="text-gray-300 text-sm">Latest gadgets</p>
                    </div>
                  </Link>
                </div>
                <div className="space-y-6 pt-12">
                  <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 hover:scale-105 hover:bg-white/15 transition-all duration-500 cursor-pointer group hover-glow" style={{ transitionDelay: '200ms' }}>
                    <div className="w-full h-32 bg-gradient-to-br from-[#8B2635] to-[#A52A3A] rounded-2xl mb-4 flex items-center justify-center overflow-hidden">
                      <i className="ri-heart-fill text-4xl text-white group-hover:scale-125 transition-transform duration-500 animate-pulse"></i>
                    </div>
                    <h3 className="text-white font-bold group-hover:text-[#D4AF37] transition-colors">For Her</h3>
                    <p className="text-gray-300 text-sm">Thoughtful ideas</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 hover:scale-105 hover:bg-white/15 transition-all duration-500 cursor-pointer group hover-glow" style={{ transitionDelay: '300ms' }}>
                    <div className="w-full h-48 bg-gradient-to-br from-[#2D5F4C] to-[#1B4332] rounded-2xl mb-4 flex items-center justify-center overflow-hidden">
                      <i className="ri-home-smile-fill text-6xl text-white group-hover:scale-110 group-hover:-rotate-6 transition-transform duration-500"></i>
                    </div>
                    <h3 className="text-white font-bold text-lg mb-2 group-hover:text-[#D4AF37] transition-colors">Home & Living</h3>
                    <p className="text-gray-300 text-sm">Cozy holiday essentials</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="flex flex-col items-center gap-2 animate-bounce cursor-pointer group" onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}>
          <span className="text-white/60 text-sm group-hover:text-white transition-colors">Scroll to explore</span>
          <i className="ri-arrow-down-line text-3xl text-white/60 group-hover:text-[#D4AF37] transition-colors"></i>
        </div>
      </div>
    </section>
  );
}
